import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import loginGoogleUseCase from '@/application/auth/loginGoogle';
import { useUserStore } from '@/stores/user/userStore';
import DFullLogo from '@/components/Global/DLogo/DFullLogo';
import DLoading from '@/components/DLoading';

// Import our new components
import LoginForm from './steps/LoginForm';
import LoginOTP from './steps/LoginOTP';
import ForgotPassword from '../Auth/ForgotPassword';

// Define our login steps
export const LoginSteps = {
  LOGIN_FORM: 'login_form',
  LOGIN_OTP: 'login_otp',
  FORGOT_PASSWORD: 'forgot_password'
};

const LogIn = () => {
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();
  const saveAuthDetail = useUserStore((state) => state.saveAuthDetail);

  // State for managing the flow
  const [currentStep, setCurrentStep] = useState(LoginSteps.LOGIN_FORM);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Page loading state
  const [pageLoading, setPageLoading] = useState(true);

  /**
   * Handles Google OAuth login
   */
  const handleGoogleLogIn = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      try {
        const rewardful_referral = window.Rewardful?.referral;
        await loginGoogleUseCase({
          ...tokenResponse,
          rewardful_referral,
          isLanding: urlParams.get('is_landing_page'),
          kb_id: urlParams.get('kb_id'),
        });

        navigate('/');
      } catch (err) {
        console.error(err);
        setError(err.message || 'An error occurred during login');
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Login Failed:', error);
      setError('Google login failed. Please try again.');
      setLoading(false);
    },
  });

  // Check if user has token in URL params
  useEffect(() => {
    if(urlParams.get('token')){
      saveAuthDetail({
        access_token: urlParams.get('token'),
      });
      navigate('/');
    }
  }, [urlParams]);

  // Simulate page loading and set pageLoading to false when all resources are loaded
  useEffect(() => {
    // Wait for the document to be fully loaded
    const handleLoad = () => {
      // Add a small delay to ensure all resources are loaded and rendered
      setTimeout(() => {
        setPageLoading(false);
      }, 500);
    };

    // If document is already loaded, call handleLoad directly
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);

      // Fallback: set pageLoading to false after a maximum timeout (2 seconds)
      const timeoutId = setTimeout(() => {
        setPageLoading(false);
      }, 2000);

      return () => {
        window.removeEventListener('load', handleLoad);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case LoginSteps.LOGIN_FORM:
        return (
          <LoginForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            handleGoogleLogIn={handleGoogleLogIn}
            goToOTP={() => setCurrentStep(LoginSteps.LOGIN_OTP)}
            goToForgotPassword={() => setCurrentStep(LoginSteps.FORGOT_PASSWORD)}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
          />
        );
      case LoginSteps.LOGIN_OTP:
        return (
          <LoginOTP
            email={email}
            password={password}
            otpCode={otpCode}
            setOtpCode={setOtpCode}
            goBack={() => setCurrentStep(LoginSteps.LOGIN_FORM)}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
          />
        );
      case LoginSteps.FORGOT_PASSWORD:
        return (
          <ForgotPassword
            email={email}
            setEmail={setEmail}
            goBack={() => setCurrentStep(LoginSteps.LOGIN_FORM)}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
          />
        );
      default:
        return (
          <LoginForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            handleGoogleLogIn={handleGoogleLogIn}
            goToOTP={() => setCurrentStep(LoginSteps.LOGIN_OTP)}
            goToForgotPassword={() => setCurrentStep(LoginSteps.FORGOT_PASSWORD)}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
          />
        );
    }
  };

  if (pageLoading) {
    return <DLoading show={true} />;
  }

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen w-full overflow-hidden">
      {/* <div className="absolute top-[-50%] left-[-20%] w-[120%] h-[120%] rounded-full bg-gradient-radial from-purple-100 to-transparent opacity-20"></div> */}
      <div className="absolute bottom-[-60%] right-[-20%] w-[120%] h-[120%] rounded-full bg-gradient-radial from-purple-200 to-transparent opacity-20"></div>

      {/* Content with entry animation */}
      <div className="relative z-10 w-full max-w-md p-4 animate-fadeIn">
        <div className="flex justify-center mb-8 h-8">
          <DFullLogo />
        </div>
        {/* Wrapper for both main content and signup link with single animation */}
        <div className="animate-fadeInUp">
          {renderStep()}

          {/* Sign Up Link - always shown */}
          <div className="bg-gray-100 py-4 rounded-b-3xl text-center shadow-lg">
            <p className="text-sm text-gray-500">
              Don't have an account? <Link to="/sign-up" className="text-indigo-600 hover:underline">Sign up</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogIn;
